import React from "react";
import "./ScientistProfile.css";
import moshePortrait from "../../../assets/substratum-protocol/scientist-moshe-mazel.webp";

const defaultScientist = {
  name: "Dr. Moshe Mazel",
  callSign: "Moshe",
  pronouns: "He/Him",
  age: "Late 20s",
  nationality: "Israeli-American",
  specialty: "Gravitational Physics / Geophysics",
  base: "Cambridge, Massachusetts",
  affiliations: "Independent Researcher",
  expeditionRole:
    "Gravity analysis, geophysical modeling, anomaly theory and interpretation",

  image: moshePortrait,

  physicalDescription:
    "Moshe is slight, precise, and perpetually thinking. He has dark wavy hair, sharp features, and observant eyes behind rectangular glasses. He dresses more like a graduate student than a field scientist—button-down shirts, rolled sleeves, worn sweaters, and sensible boots.",

  physicalQuote: "He looks like someone who forgets to eat.",

  personality:
    "Brilliant, intense, idealistic, restless, and guilt-prone. Moshe is driven by a deep need to understand the rules that govern the world—and the frightening possibility that those rules are not as stable as everyone believes.",

  strengths: [
    "Exceptional theoretical mind",
    "Understands complex systems",
    "Sees patterns others miss",
    "Relentless intellectual curiosity",
    "Comfortable with uncertainty",
  ],

  weaknesses: [
    "Struggles with authority",
    "Burdened by guilt and secrecy",
    "Physically less rugged than other scientists",
    "Overthinks under pressure",
    "Finds it hard to trust institutions",
  ],

  recruitment: {
    title: "How Did He End Up on the Team?",
    paragraphs: [
      "Moshe is the youngest member of the team and was not the obvious first choice. However, his doctoral dissertation on gravitational fluctuation suddenly became relevant as the strange phenomena affecting Earth accelerated.",
      "His former professor defended Moshe's unconventional ideas when the dissertation received significant criticism. That recommendation ultimately helped secure Moshe a place on the Substratum Expedition Team.",
    ],
  },

  research: {
    title: "What Kind of Research Has He Done in the Past?",
    paragraphs: [
      "Moshe's research focuses on gravitational fluctuation and geophysical dynamics. His doctoral dissertation began as a rigorous examination of gravitational poles and the phenomenon of polar alignment.",
      "Then the dissertation took a sharp turn. Moshe argued that gravitational poles are not necessarily fixed and that, under certain conditions, they could shift rapidly.",
      "His model predicted a catastrophic reversal: North could become South, South could become North, and the resulting disruption could affect the entire planet.",
      "At the time, his conclusions were dismissed as alarmist and unconventional. Now, with Earth's gravitational behavior becoming increasingly strange, his work appears disturbingly prescient.",
    ],
  },

  dilemma: {
    title: "What Was a Moral Work Dilemma and How Did He Handle It?",
    paragraphs: [
      "Moshe's ideas did not come entirely from legitimate academic research. In his pursuit of answers, he secretly entered restricted archives and stole part of a classified research program.",
      "The material contained information concerning gravitational instability and research that had never been intended for public release.",
      "Moshe knew what he had done was unethical. He also believed the information might be too important to ignore. He incorporated what he discovered into his dissertation and never revealed the source.",
      "He has carried the guilt ever since.",
    ],
  },

  background: {
    title: "Personal Background",
    paragraphs: [
      "Moshe's academic career has been defined by being the person in the room willing to take an idea one step further than everyone else.",
      "His dissertation nearly ended his career before it began. Instead, his professor's support allowed him to earn his doctorate—and may have inadvertently placed him in the exact position he needs to be in now.",
    ],
  },

  interests: {
    title: "Personal Interests & Hobbies",
    paragraphs: [
      "Moshe has few hobbies that aren't connected to his work. He reads old scientific papers, collects notebooks, and enjoys problems that have no immediately obvious solution.",
      "He has an appreciation for philosophy and theoretical physics, particularly questions concerning whether physical laws are fundamental or merely temporary descriptions of reality.",
    ],
  },

  skills: [
    {
      name: "THINK",
      die: "d10",
      rationale: "Exceptional theoretical thinker and analyst",
    },
    {
      name: "BREAK",
      die: "d6",
      rationale: "Understands complex systems and failure points",
    },
    {
      name: "SWAY",
      die: "d4",
      rationale: "His paranoia and shyness affect his ability to sway others.",
    },
    {
      name: "MAKE",
      die: "d4",
      rationale: "Not his primary area of expertise",
    },
    {
      name: "DASH",
      die: "d8",
      rationale:
        "His survival instincts are strong, allowing him to navigate dangerous situations effectively.",
    },
    {
      name: "EVADE",
      die: "d4",
      rationale: "Not especially evasive",
    },
  ],

  roleplaying: {
    voice: "Measured, precise, occasionally soft-spoken",
    demeanor: "Focused, thoughtful, sometimes distant",
    humor: "Dry, intellectual, self-deprecating",
    dangerResponse: "I'd rather be wrong than unprepared.",
    hook: "Moshe believes the worst is coming—and that understanding the physics behind it is humanity's only chance.",
    quote: "The poles will move. We just don't know when.",
  },
};

function Section({ title, children, className = "" }) {
  return (
    <section className={`profile-section ${className}`}>
      <h2>{title}</h2>
      {children}
    </section>
  );
}

function Paragraphs({ paragraphs = [] }) {
  return (
    <>
      {paragraphs.map((paragraph, index) => (
        <p key={index}>{paragraph}</p>
      ))}
    </>
  );
}

function ScientistProfile_Moshe({ scientist = defaultScientist }) {
  return (
    <article className="scientist-profile">
      {/* =========================
          LEFT COLUMN
      ========================== */}
      <aside className="profile-sidebar">
        {/* Portrait */}
        <div className="portrait-frame">
          <img src={scientist.image} alt={`${scientist.name} portrait`} />
        </div>

        {/* Physical Description */}
        <Section title="Physical Description">
          <p>{scientist.physicalDescription}</p>

          {scientist.physicalQuote && (
            <p className="profile-quote-small">{scientist.physicalQuote}</p>
          )}
        </Section>

        {/* Personality */}
        <Section title="Personality Profile">
          <p>{scientist.personality}</p>
        </Section>

        {/* Strengths / Weaknesses */}
        <Section title="Strengths & Weaknesses">
          <div className="strength-group">
            <h3>Strengths</h3>

            <ul>
              {scientist.strengths.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>

          <div className="weakness-group">
            <h3>Weaknesses</h3>

            <ul>
              {scientist.weaknesses.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
        </Section>

        {/* Skills */}
        <Section title="Substratum Skills">
          <table className="skills-table">
            <thead>
              <tr>
                <th>Skill</th>
                <th>Die</th>
                <th>Rationale</th>
              </tr>
            </thead>

            <tbody>
              {scientist.skills.map((skill) => (
                <tr key={skill.name}>
                  <td className="skill-name">{skill.name}</td>
                  <td className="skill-die">{skill.die}</td>
                  <td>{skill.rationale}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <p className="skills-note">
            Standard character creation uses one d10, one d8, one d6, and three
            d4s.
          </p>
        </Section>
      </aside>

      {/* =========================
          RIGHT COLUMN
      ========================== */}
      <main className="profile-main">
        {/* Header */}
        <header className="profile-header">
          <div className="eyebrow">PERSONNEL OVERVIEW</div>

          <h1>{scientist.name}</h1>

          <div className="personnel-info">
            <div className="info-row">
              <strong>Call Sign:</strong>
              <span>{scientist.callSign}</span>
            </div>

            <div className="info-row">
              <strong>Pronouns:</strong>
              <span>{scientist.pronouns}</span>
            </div>

            <div className="info-row">
              <strong>Age:</strong>
              <span>{scientist.age}</span>
            </div>

            <div className="info-row">
              <strong>Nationality:</strong>
              <span>{scientist.nationality}</span>
            </div>

            <div className="info-row">
              <strong>Scientific Specialty:</strong>
              <span>{scientist.specialty}</span>
            </div>

            <div className="info-row">
              <strong>Current Base:</strong>
              <span>{scientist.base}</span>
            </div>

            <div className="info-row">
              <strong>Affiliations:</strong>
              <span>{scientist.affiliations}</span>
            </div>

            <div className="info-row">
              <strong>Expedition Role:</strong>
              <span>{scientist.expeditionRole}</span>
            </div>
          </div>
        </header>

        {/* Recruitment */}
        <Section title={scientist.recruitment.title}>
          <Paragraphs paragraphs={scientist.recruitment.paragraphs} />
        </Section>

        {/* Research */}
        <Section title={scientist.research.title}>
          <Paragraphs paragraphs={scientist.research.paragraphs} />
        </Section>

        {/* Moral Dilemma */}
        <Section title={scientist.dilemma.title}>
          <Paragraphs paragraphs={scientist.dilemma.paragraphs} />
        </Section>

        {/* Background */}
        <Section title={scientist.background.title}>
          <Paragraphs paragraphs={scientist.background.paragraphs} />
        </Section>

        {/* Interests */}
        <Section title={scientist.interests.title}>
          <Paragraphs paragraphs={scientist.interests.paragraphs} />
        </Section>

        {/* </div> */}
      </main>
    </article>
  );
}

export default ScientistProfile_Moshe;
